import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  PROPOSAL_STATUS, VERSION_KIND, emptyInput, projectProposalsApi, type ProjectProposalDetail, type ProjectProposalInput,
} from '../../api/projectProposals'
import {
  Button, Callout, Card, Dialog, IconButton, LinearProgress, LoadingState, OverflowMenu, Page, PageHeader, PaneLayout, StatusPill,
  TextField, useConfirm, useSnackbar,
} from '../../components/ui'
import { ProposalForm } from './ProposalForm'
import './projectProposals.css'

const BACK = { to: '/project-proposals', label: 'Pagine progetto' }
const POLL_MS = 4000
const message = (e: unknown, fallback: string) => e instanceof Error ? e.message : fallback
const time = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

// L'anteprima gira in un iframe isolato (origine opaca), che non sempre può caricare le immagini del CRM: logo e grana
// entrano nella pagina come data URL. Sul sito dei progetti restano file normali.
const PREVIEW_ASSETS = ['/progetti-assets/logo-white.png', '/progetti-assets/grain.png']
let inlined: Promise<[string, string][]> | null = null
function inlineAssets(html: string): Promise<string> {
  inlined ??= Promise.all(PREVIEW_ASSETS.map(async (path): Promise<[string, string]> => {
    const blob = await (await fetch(path)).blob()
    const data = await new Promise<string>(res => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(blob) })
    return [path, data]
  })).catch(() => { inlined = null; return [] })
  return inlined.then(pairs => pairs.reduce((h, [path, data]) => h.split(path).join(data), html))
}

/** Pagina progetto: con `/new` i dati per la prima generazione, altrimenti anteprima, chat delle modifiche, versioni e pubblicazione. */
export function ProjectProposalPage() {
  const { id } = useParams()
  return id === undefined ? <NewProposal /> : <ProposalDetail id={Number(id)} />
}

function NewProposal() {
  const navigate = useNavigate()
  const [input, setInput] = useState<ProjectProposalInput>(emptyInput)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const d = await projectProposalsApi.create({ ...input, slug: input.slug?.trim() || null })
      navigate(`/project-proposals/${d.id}`, { replace: true })
    } catch (err) {
      setError(message(err, 'Creazione non riuscita.'))
      setSaving(false)
    }
  }

  return (
    <Page>
      <PageHeader title="Nuova pagina progetto" icon="auto_awesome" back={BACK} />
      <form onSubmit={submit} className="d-flex flex-column gap-4">
        <ProposalForm value={input} onChange={setInput} />
        {error && <Callout tone="error">{error}</Callout>}
        <div className="d-flex justify-content-end">
          <Button type="submit" variant="filled" icon="auto_awesome" loading={saving}>Scrivi la pagina</Button>
        </div>
      </form>
    </Page>
  )
}

function ProposalDetail({ id }: { id: number }) {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const askConfirm = useConfirm()
  const [detail, setDetail] = useState<ProjectProposalDetail | null>(null)
  const [error, setError] = useState('')
  const [viewing, setViewing] = useState<number | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [instruction, setInstruction] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<ProjectProposalInput | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [password, setPassword] = useState('')
  const [dialogError, setDialogError] = useState('')
  const chatLog = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      setDetail(await projectProposalsApi.get(id))
    } catch (e) {
      setError(message(e, 'Errore nel caricamento.'))
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  // Mentre l'IA lavora si ricontrolla ogni pochi secondi: generazione e modifiche girano in coda sul server.
  const working = detail?.working ?? false
  useEffect(() => {
    if (!working) return
    const t = setInterval(() => { void load() }, POLL_MS)
    return () => clearInterval(t)
  }, [working, load])

  const shown = viewing ?? detail?.currentVersion ?? 0
  const hasContent = detail?.hasContent ?? false
  useEffect(() => {
    if (!hasContent || !shown) return
    let alive = true
    projectProposalsApi.page(id, shown)
      .then(r => inlineAssets(r.html))
      .then(h => { if (alive) setHtml(h) })
      .catch(e => { if (alive) setError(message(e, 'Anteprima non disponibile.')) })
    return () => { alive = false }
  }, [id, shown, hasContent])

  const versionCount = detail?.versions.length ?? 0
  // Solo il registro della chat scorre all'ultimo messaggio: la pagina resta dov'è.
  useEffect(() => { const log = chatLog.current; if (log) log.scrollTop = log.scrollHeight }, [versionCount, working])

  async function run(action: () => Promise<ProjectProposalDetail>, fallback: string) {
    setBusy(true)
    setError('')
    try {
      setDetail(await action())
      setViewing(null)
      return true
    } catch (e) {
      setError(message(e, fallback))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault()
    const text = instruction.trim()
    if (!text) return
    if (await run(() => projectProposalsApi.revise(id, text), 'Richiesta non inviata.')) setInstruction('')
  }

  async function saveInput(regenerate: boolean) {
    if (!editing) return
    setBusy(true)
    setDialogError('')
    try {
      setDetail(await projectProposalsApi.updateInput(id, editing, regenerate))
      setViewing(null)
      setEditing(null)
      snackbar.show({ message: regenerate ? 'L’IA riscrive la pagina' : 'Dati aggiornati' })
    } catch (err) {
      setDialogError(message(err, 'Salvataggio non riuscito.'))
    } finally {
      setBusy(false)
    }
  }

  async function publish(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setDialogError('')
    try {
      const r = await projectProposalsApi.publish(id, password.trim() || null)
      setDetail(r.detail)
      setPublishing(false)
      setPassword('')
      snackbar.show({ message: 'Pagina pubblicata' })
    } catch (err) {
      setDialogError(message(err, 'Pubblicazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!await askConfirm({ title: 'Eliminare la pagina progetto?', message: 'Sparisce dal CRM con tutte le versioni. Se è già pubblicata, sul sito resta.', confirmLabel: 'Elimina', destructive: true })) return
    try {
      await projectProposalsApi.remove(id)
      snackbar.show({ message: 'Pagina progetto eliminata' })
      navigate('/project-proposals', { replace: true })
    } catch (e) {
      setError(message(e, 'Eliminazione non riuscita.'))
    }
  }

  if (!detail) {
    return <Page><PageHeader title="Pagina progetto" back={BACK} />{error ? <Callout tone="error">{error}</Callout> : <LoadingState />}</Page>
  }

  const firstPublish = detail.publishedVersion == null
  const status = PROPOSAL_STATUS[detail.status]
  const locked = detail.working || busy

  const preview = (
    <Card padding="none" className="pp-preview">
      {detail.hasContent && html ? (
        <iframe title={`Anteprima della pagina di ${detail.clientName}`} srcDoc={html} sandbox="allow-scripts" className="pp-preview__frame" />
      ) : (
        <div className="pp-preview__empty">
          {detail.working ? <><LinearProgress /><p>L’IA sta scrivendo la pagina: di solito ci vogliono uno o due minuti.</p></> : <p>La pagina non è ancora stata scritta.</p>}
        </div>
      )}
      {viewing !== null && viewing !== detail.currentVersion && (
        <div className="pp-preview__bar">
          <span>Stai guardando la versione {viewing}.</span>
          <Button variant="text" onClick={() => setViewing(null)}>Torna all’ultima</Button>
          <Button variant="tonal" icon="history" disabled={locked} onClick={() => run(() => projectProposalsApi.restore(id, viewing), 'Ripristino non riuscito.')}>
            Ripristina questa
          </Button>
        </div>
      )}
    </Card>
  )

  const chat = (
    <Card className="pp-chat" padding="none">
      <div className="pp-chat__log" aria-live="polite" ref={chatLog}>
        {detail.versions.map(v => (
          <div key={v.number} className="pp-chat__turn">
            {v.instruction && <div className="pp-msg pp-msg--me">{v.instruction}</div>}
            <div className={`pp-msg pp-msg--ai${shown === v.number ? ' is-shown' : ''}`}>
              <div className="pp-msg__head">
                <strong>v{v.number}</strong> · {VERSION_KIND[v.kind]} · {time(v.createdAt)}
                {detail.publishedVersion === v.number && <StatusPill tone="success">Online</StatusPill>}
              </div>
              {v.summary && <div>{v.summary}</div>}
              {shown !== v.number && <Button variant="text" size="sm" onClick={() => setViewing(v.number)}>Vedi</Button>}
            </div>
          </div>
        ))}
        {detail.working && (
          <div className="pp-chat__turn">
            {detail.pendingInstruction && <div className="pp-msg pp-msg--me">{detail.pendingInstruction}</div>}
            <div className="pp-msg pp-msg--ai"><LinearProgress /> Sto scrivendo…</div>
          </div>
        )}
      </div>
      {detail.error && !detail.working && (
        <Callout tone="error">
          <div className="d-flex flex-column align-items-start gap-2">
            {detail.error}
            {/* Senza pagina non c'è chat da cui riprovare: si riscrive dagli stessi dati. */}
            {!detail.hasContent && (
              <Button variant="tonal" icon="refresh" loading={busy}
                onClick={() => run(() => projectProposalsApi.updateInput(id, detail.input, true), 'Nuovo tentativo non riuscito.')}>
                Riprova
              </Button>
            )}
          </div>
        </Callout>
      )}
      <form className="pp-chat__send" onSubmit={send}>
        <TextField
          label="Cosa cambio?"
          labelHidden
          multiline
          rows={2}
          placeholder={detail.hasContent ? 'Es. «accorcia l’introduzione», «aggiungi una FAQ sui tempi»' : 'Disponibile quando la pagina è pronta'}
          value={instruction}
          disabled={!detail.hasContent || locked}
          maxLength={2000}
          onChange={e => setInstruction(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.form?.requestSubmit() } }}
        />
        <IconButton type="submit" icon="send" variant="filled" aria-label="Invia la richiesta" disabled={!instruction.trim() || !detail.hasContent || locked} />
      </form>
    </Card>
  )

  return (
    <Page>
      <PageHeader
        title={detail.clientName}
        subtitle={<>progetti.equalstudio.it/{detail.slug} · <StatusPill tone={status.tone}>{status.label}</StatusPill></>}
        icon="auto_awesome"
        back={BACK}
        actions={
          <>
            <Button variant="outlined" icon="edit_note" disabled={locked} onClick={() => { setDialogError(''); setEditing(detail.input) }}>Dati</Button>
            <Button variant="filled" icon="publish" disabled={locked || !detail.hasContent} onClick={() => { setDialogError(''); setPublishing(true) }}>
              {firstPublish ? 'Pubblica' : 'Aggiorna online'}
            </Button>
            <OverflowMenu aria-label="Altre azioni" items={[{ label: 'Elimina', icon: 'delete', onSelect: () => { void remove() } }]} />
          </>
        }
      />
      <div className="d-flex flex-column gap-3">
        {error && <Callout tone="error">{error}</Callout>}
        {detail.publicUrl && (
          <Callout tone="info">
            Online: <a href={detail.publicUrl} target="_blank" rel="noreferrer">{detail.publicUrl}</a>
            {detail.publishedVersion !== detail.currentVersion && ` · è online la versione ${detail.publishedVersion}, le modifiche successive non ancora.`}
          </Callout>
        )}
        <PaneLayout variant="supporting" main={preview} pane={chat} mainLabel="Anteprima" paneLabel="Modifiche" />
      </div>

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Dati della pagina"
        size="lg"
        supportingText="Cliente, fee e pacchetti cambiano subito sulla pagina. Per riscrivere i testi dai nuovi dati, «Riscrivi la pagina»."
        actions={
          <>
            <Button variant="text" dismiss onClick={() => setEditing(null)}>Annulla</Button>
            <Button variant="outlined" loading={busy} onClick={() => saveInput(true)}>Riscrivi la pagina</Button>
            <Button variant="filled" loading={busy} disabled={!detail.hasContent} onClick={() => saveInput(false)}>Aggiorna i dati</Button>
          </>
        }
      >
        {editing && <ProposalForm value={editing} onChange={setEditing} slugLocked />}
        {dialogError && <Callout tone="error">{dialogError}</Callout>}
      </Dialog>

      <Dialog
        open={publishing}
        onClose={() => setPublishing(false)}
        title={firstPublish ? 'Pubblica la pagina' : 'Aggiorna la pagina online'}
        as="form"
        onSubmit={publish}
        supportingText={firstPublish
          ? `La pagina va su progetti.equalstudio.it/${detail.slug}, protetta da una password da dare al cliente.`
          : 'Pubblica la versione attuale. Lascia vuota la password per tenere quella che il cliente ha già.'}
        actions={
          <>
            <Button variant="text" dismiss onClick={() => setPublishing(false)}>Annulla</Button>
            <Button type="submit" variant="filled" loading={busy} disabled={firstPublish && password.trim().length < 6}>Pubblica</Button>
          </>
        }
      >
        {!detail.publishConfigured && <Callout tone="warning">La pubblicazione sul sito dei progetti non è ancora configurata sul server.</Callout>}
        <TextField
          label={firstPublish ? 'Password per il cliente' : 'Nuova password (facoltativa)'}
          type="text"
          autoComplete="off"
          minLength={6}
          maxLength={100}
          supportingText="Almeno 6 caratteri. Il sito la conserva cifrata: non si può rileggere."
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
        {dialogError && <Callout tone="error">{dialogError}</Callout>}
      </Dialog>
    </Page>
  )
}
